import { format, isValid, parseISO } from 'date-fns';
import { castArray, concat, filter as lodashFilter, find, findIndex, isNil, map, reduce, some, sortBy, uniq } from 'lodash';

import { getProgrammaticTypeDetailsMap } from 'utils/programmaticTaskConfig';
import { formatProtocolsInstanceStatus } from 'utils/protocolsFormatters';

/*
The task list filter contract (SYN-87): every chip is { field, operator, value }.
This is the client copy of synergrx-server/src/task/list/filter-definitions.ts —
the server is the source of truth for which fields and operators exist and what a
value looks like; this file adds the labels, groups and option lists the menu needs.
Keep the two in sync like the other constants-from-client files.
*/

export const FILTER_GROUPS = [
    { key: 'task', label: 'Task' },
    { key: 'people', label: 'People' },
    { key: 'patient', label: 'Patient' },
    { key: 'dates', label: 'Time and dates' }
];

// valueKind: none (no value), single, multi (array), range (two-element array)
export const OPERATORS = {
    contains: { label: 'contains', valueKind: 'single' },
    notContains: { label: 'does not contain', valueKind: 'single' },
    is: { label: 'is', valueKind: 'single' },
    isNot: { label: 'is not', valueKind: 'single' },
    startsWith: { label: 'starts with', valueKind: 'single' },
    isEmpty: { label: 'is empty', valueKind: 'none' },
    isNotEmpty: { label: 'is not empty', valueKind: 'none' },
    isAnyOf: { label: 'is any of', valueKind: 'multi' },
    isNoneOf: { label: 'is none of', valueKind: 'multi' },
    isMe: { label: 'is me', valueKind: 'none' },
    before: { label: 'is before', valueKind: 'single' },
    onOrBefore: { label: 'is on or before', valueKind: 'single' },
    after: { label: 'is after', valueKind: 'single' },
    onOrAfter: { label: 'is on or after', valueKind: 'single' },
    between: { label: 'is between', valueKind: 'range' },
    today: { label: 'is today', valueKind: 'none' },
    onOrBeforeToday: { label: 'is on or before today', valueKind: 'none' },
    overdue: { label: 'is overdue', valueKind: 'none' },
    next7Days: { label: 'is in the next 7 days', valueKind: 'none' },
    thisWeek: { label: 'is this week', valueKind: 'none' },
    equals: { label: 'equals', valueKind: 'single' },
    lessThan: { label: 'is less than', valueKind: 'single' },
    greaterThan: { label: 'is greater than', valueKind: 'single' },
    isYes: { label: 'is yes', valueKind: 'none' },
    isNo: { label: 'is no', valueKind: 'none' }
};

const TEXT_OPERATORS = ['contains', 'notContains', 'is', 'isNot', 'startsWith', 'isEmpty'];
const TEXT_OPERATORS_WITHOUT_EMPTY = TEXT_OPERATORS.filter((operator) => operator !== 'isEmpty');
// Comment is the task's comment thread: "contains" is any comment matching, the empty operators are whether it has any.
const COMMENT_OPERATORS = ['contains', 'notContains', 'isEmpty', 'isNotEmpty'];
const LIST_OPERATORS = ['is', 'isNot', 'isAnyOf', 'isNoneOf'];
// Protocol picks templates from a list, and "is empty" is the clinic tasks themselves.
const PROTOCOL_OPERATORS = [...LIST_OPERATORS, 'isEmpty'];
const PERSON_OPERATORS = ['is', 'isNot', 'isAnyOf', 'isMe'];
const DATE_OPERATORS = [
    'today',
    'onOrBeforeToday',
    'overdue',
    'next7Days',
    'thisWeek',
    'is',
    'before',
    'onOrBefore',
    'after',
    'onOrAfter',
    'between'
];
const CREATED_AT_OPERATORS = DATE_OPERATORS.filter((operator) => !['overdue', 'next7Days', 'onOrBeforeToday'].includes(operator));
const MINUTES_OPERATORS = ['equals', 'lessThan', 'greaterThan', 'between'];
const YES_NO_OPERATORS = ['isYes', 'isNo'];

export const TASK_STATUS_OPTIONS = [
    { value: 'OPEN', label: 'Open' },
    { value: 'CLOSED', label: 'Closed' }
];

export const TASK_SCOPE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'base', label: 'Tasks' },
    { value: 'protocols', label: 'Protocols' }
];

const PROTOCOL_STATUS_OPTIONS = ['created', 'in_draft', 'needs_review', 'submitted', 'finalized', 'closed'].map((status) => ({
    value: status,
    label: formatProtocolsInstanceStatus(status)
}));

// UNVERIFIED_MANUAL_FILL is also a typed task - it is listed once, under the typed tasks below
const PROG_TASK_TYPES_EXCLUDED_FROM_FILTER = ['CUSTOM_PROTOCOLS', 'UNVERIFIED_MANUAL_FILL'];

const CLINIC_TYPED_TASK_OPTIONS = [
    { value: 'MEDICATION_REFILL', label: 'Medication Refill Follow-Up' },
    { value: 'RX_REFILL', label: 'Prescription Refill Needed' },
    { value: 'MEDHX_MISSING_DATA', label: 'Patient Info Needed for Fill Import' },
    { value: 'MEDHX_PROCESS_IMPORT', label: 'Fill Import Reconciliation Required' },
    { value: 'CALL_PATIENT_FOR_PICKUP', label: 'Call Patient for Pickup Date' },
    { value: 'UNVERIFIED_MANUAL_FILL', label: 'Unverified Manual Fill' }
];

// term: the org's program labels (hooks/useTerminology)
export function getTaskTypeOptions(term) {
    const programmatic = lodashFilter(
        map(getProgrammaticTypeDetailsMap(term), (progTask, key) => ({ value: key, label: `Programmatic ${progTask.name}` })),
        (option) => !PROG_TASK_TYPES_EXCLUDED_FROM_FILTER.includes(option.value)
    );
    return concat(programmatic, CLINIC_TYPED_TASK_OPTIONS);
}

/*
type: text | list | person | date | minutes | yesNo
options: a fixed list, a function of term, or the name of a store-backed source
  ('clinicMembers' | 'teams' | 'protocolTemplates') that useFilterLabels resolves
clinicOnly: hidden on the patient Tasks tab, where the field is fixed or meaningless
*/
export const TASK_FILTER_FIELDS = [
    { key: 'status', group: 'task', label: 'Status', type: 'list', operators: LIST_OPERATORS, options: TASK_STATUS_OPTIONS },
    { key: 'scope', group: 'task', label: 'Scope', type: 'list', operators: ['is'], options: TASK_SCOPE_OPTIONS },
    { key: 'name', group: 'task', label: 'Name', type: 'text', operators: TEXT_OPERATORS_WITHOUT_EMPTY },
    { key: 'detail', group: 'task', label: 'Detail', type: 'text', operators: TEXT_OPERATORS_WITHOUT_EMPTY },
    { key: 'closeNote', group: 'task', label: 'Close note', type: 'text', operators: TEXT_OPERATORS },
    { key: 'comment', group: 'task', label: 'Comment', type: 'text', operators: COMMENT_OPERATORS },
    { key: 'type', group: 'task', label: 'Type', type: 'list', operators: LIST_OPERATORS, options: getTaskTypeOptions },
    { key: 'protocol', group: 'task', label: 'Protocol', type: 'list', operators: PROTOCOL_OPERATORS, options: 'protocolTemplates' },
    {
        key: 'protocolStatus',
        group: 'task',
        label: 'Protocol status',
        type: 'list',
        operators: LIST_OPERATORS,
        options: PROTOCOL_STATUS_OPTIONS
    },
    { key: 'assignedTo', group: 'people', label: 'Assigned to', type: 'person', operators: PERSON_OPERATORS, options: 'clinicMembers' },
    { key: 'createdBy', group: 'people', label: 'Created by', type: 'person', operators: PERSON_OPERATORS, options: 'clinicMembers' },
    { key: 'patient', group: 'patient', label: 'Patient', type: 'text', operators: TEXT_OPERATORS, clinicOnly: true },
    {
        key: 'patientTeam',
        group: 'patient',
        label: 'Patient team',
        type: 'list',
        operators: LIST_OPERATORS,
        options: 'teams',
        clinicOnly: true
    },
    { key: 'carePaused', group: 'patient', label: 'Care paused', type: 'yesNo', operators: YES_NO_OPERATORS, clinicOnly: true },
    {
        key: 'ccmInteractionMtd',
        group: 'patient',
        label: (term) => `${term('ccm')} Patient Interaction MTD`,
        type: 'yesNo',
        operators: YES_NO_OPERATORS,
        clinicOnly: true
    },
    {
        key: 'ccmMtd',
        group: 'patient',
        label: (term) => `${term('ccm')} MTD`,
        type: 'minutes',
        operators: MINUTES_OPERATORS,
        clinicOnly: true
    },
    {
        key: 'rpmMtd',
        group: 'patient',
        label: (term) => `${term('rpm')} MTD`,
        type: 'minutes',
        operators: MINUTES_OPERATORS,
        clinicOnly: true
    },
    { key: 'dueDate', group: 'dates', label: 'Due date', type: 'date', operators: DATE_OPERATORS },
    { key: 'createdAt', group: 'dates', label: 'Created at', type: 'date', operators: CREATED_AT_OPERATORS }
];

export function getFilterField(key) {
    return find(TASK_FILTER_FIELDS, { key });
}

export function getFieldLabel(field, term) {
    return typeof field.label === 'function' ? field.label(term) : field.label;
}

// The kind of value a field takes, as the Filter menu shows it beside the field name.
const FIELD_TYPE_HINTS = {
    list: 'list',
    text: 'text',
    person: 'person',
    date: 'date',
    minutes: 'minutes',
    yesNo: 'yes / no'
};

export function getFieldTypeHint(field) {
    return FIELD_TYPE_HINTS[field.type] ?? field.type;
}

export function getOperatorValueKind(operator) {
    return OPERATORS[operator]?.valueKind;
}

// The fields the menu offers in one place: the patient tab hides the ones that
// describe the patient, since the list is already pinned to one.
export function getAvailableFilterFields(lockedToPatient) {
    return TASK_FILTER_FIELDS.filter((field) => !(lockedToPatient && field.clinicOnly));
}

export const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const CALENDAR_DATE_FORMAT = 'yyyy-MM-dd';

function isCalendarDate(value) {
    return typeof value === 'string' && CALENDAR_DATE_PATTERN.test(value) && isValid(parseISO(value));
}

function isPositiveInteger(value) {
    return Number.isInteger(value) && value > 0;
}

function isMinutes(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

// The server's limits (filter-definitions.ts): a chip past them is a 400 for the whole list.
export const MAX_FILTERS = 20;
export const MAX_TEXT_LENGTH = 255;
export const MAX_LIST_VALUES = 50;

function isText(value) {
    return typeof value === 'string' && value.trim().length > 0 && value.length <= MAX_TEXT_LENGTH;
}

// store-backed lists whose values are row ids rather than option keys
const ID_BACKED_OPTION_SOURCES = ['teams', 'protocolTemplates'];

function isValidScalar(field, value) {
    switch (field.type) {
        case 'text':
            return isText(value);
        case 'list':
            if (ID_BACKED_OPTION_SOURCES.includes(field.options)) {
                return isPositiveInteger(value);
            }
            if (Array.isArray(field.options)) {
                return some(field.options, { value });
            }
            // option lists built from terminology (task types) are checked by key shape only
            return isText(value);
        case 'person':
            return isPositiveInteger(value);
        case 'date':
            return isCalendarDate(value);
        case 'minutes':
            return isMinutes(value);
        default:
            return false;
    }
}

// True when a chip has a field, an operator that field takes and a value of the
// right shape — the only chips that are ever applied or put in the URL.
export function isFilterComplete(filter) {
    const field = getFilterField(filter?.field);
    if (!field || !field.operators.includes(filter.operator)) {
        return false;
    }
    const kind = getOperatorValueKind(filter.operator);
    const { value } = filter;
    switch (kind) {
        case 'none':
            return true;
        case 'single':
            return isValidScalar(field, value);
        case 'multi':
            return (
                Array.isArray(value) &&
                value.length > 0 &&
                value.length <= MAX_LIST_VALUES &&
                value.every((item) => isValidScalar(field, item))
            );
        case 'range':
            return Array.isArray(value) && value.length === 2 && value.every((item) => isValidScalar(field, item)) && value[0] <= value[1];
        default:
            return false;
    }
}

// Chips combine with AND. Most fields take a single chip, and picking one again edits it:
// a second "is" on a list is redundant or contradictory ("is any of" says either), and two
// date or minutes chips either restate "is between" or quietly narrow the list (picking
// "this week" beside "on or before today" reads as switching, not as both). Only text
// comparisons are genuine conjunctions (Name contains "refill" and does not contain
// "test"), so only text fields may repeat.
export function isRepeatableField(field) {
    return field.type === 'text';
}

const MEMBERSHIP_OPERATORS = ['is', 'isAnyOf'];

// Two membership chips on one field fold into "is any of" (or "is" when they agree);
// anything else (an "is not", or a field without "is any of") keeps the first chip.
function mergeSameFieldChips(field, first, second) {
    const bothMembership = MEMBERSHIP_OPERATORS.includes(first.operator) && MEMBERSHIP_OPERATORS.includes(second.operator);
    if (!bothMembership || !field.operators.includes('isAnyOf')) {
        return first;
    }
    const values = uniq(concat(castArray(first.value), castArray(second.value)));
    if (values.length === 1) {
        return { field: field.key, operator: 'is', value: values[0] };
    }
    return { field: field.key, operator: 'isAnyOf', value: values };
}

// One chip per single-choice field, however the list was built (a hand-edited or stale
// URL can still carry duplicates); the chip keeps the first one's position.
export function dedupeFilters(filters) {
    return reduce(
        filters,
        (kept, chip) => {
            const field = getFilterField(chip.field);
            const existingIndex = findIndex(kept, { field: chip.field });
            if (!field || isRepeatableField(field) || existingIndex === -1) {
                return [...kept, chip];
            }
            const merged = mergeSameFieldChips(field, kept[existingIndex], chip);
            return kept.map((existing, index) => (index === existingIndex ? merged : existing));
        },
        []
    );
}

// Strips a chip down to the contract shape (no stray value on a no-value operator).
export function normalizeFilter(filter) {
    const kind = getOperatorValueKind(filter.operator);
    if (kind === 'none') {
        return { field: filter.field, operator: filter.operator };
    }
    return { field: filter.field, operator: filter.operator, value: filter.value };
}

// Adds chips to the ones on the page the way the AI preview applies: a chip for a
// single-choice field replaces that field's chip in place, anything else is appended.
// Incomplete chips are dropped. mergeFilters keeps the server's chip limit; the
// unlimited form lets the caller say when that cut something.
export function mergeFiltersWithoutLimit(existing, incoming) {
    return reduce(
        incoming.filter(isFilterComplete).map(normalizeFilter),
        (kept, chip) => {
            const field = getFilterField(chip.field);
            const existingIndex = findIndex(kept, { field: chip.field });
            if (isRepeatableField(field) || existingIndex === -1) {
                return [...kept, chip];
            }
            return kept.map((current, index) => (index === existingIndex ? chip : current));
        },
        existing
    );
}

export function mergeFilters(existing, incoming) {
    return mergeFiltersWithoutLimit(existing, incoming).slice(0, MAX_FILTERS);
}

export function serializeFilters(filters) {
    return JSON.stringify(filters.map(normalizeFilter));
}

// Reads the `filters` URL parameter; chips that don't fit the contract are dropped
// rather than failing the page, so a stale or hand-edited link still opens. Merging
// duplicates can push a list past MAX_LIST_VALUES, so chips are checked again after.
export function parseFiltersParam(raw) {
    if (!raw) {
        return [];
    }
    let decoded;
    try {
        decoded = JSON.parse(raw);
    } catch (e) {
        return [];
    }
    if (!Array.isArray(decoded)) {
        return [];
    }
    return dedupeFilters(decoded.filter(isFilterComplete).map(normalizeFilter)).filter(isFilterComplete).slice(0, MAX_FILTERS);
}

export function buildTasksSearch(filters) {
    const params = new URLSearchParams();
    if (filters.length > 0) {
        params.set('filters', serializeFilters(filters));
    }
    return params.toString();
}

// The clinic Tasks page with these chips applied.
export function tasksPagePath(clinicId, filters) {
    const search = buildTasksSearch(filters);
    return search ? `/clinic/${clinicId}/tasks?${search}` : `/clinic/${clinicId}/tasks`;
}

// A patient's Tasks tab with these chips applied.
export function patientTasksPagePath(clinicId, patientId, filters) {
    const path = `/clinic/${clinicId}/patient/${patientId}/edit/tasks/details`;
    const search = buildTasksSearch(filters);
    return search ? `${path}?${search}` : path;
}

// Chips the in-app deep links (homepage cards, sidebar, patient tab) are built from.
export const CHIPS = {
    statusOpen: { field: 'status', operator: 'is', value: 'OPEN' },
    statusClosed: { field: 'status', operator: 'is', value: 'CLOSED' },
    assignedToMe: { field: 'assignedTo', operator: 'isMe' },
    dueOnOrBeforeToday: { field: 'dueDate', operator: 'onOrBeforeToday' },
    scopeTasks: { field: 'scope', operator: 'is', value: 'base' },
    scopeProtocols: { field: 'scope', operator: 'is', value: 'protocols' },
    typeIs: (typeKey) => ({ field: 'type', operator: 'is', value: typeKey }),
    patientTeamIs: (teamId) => ({ field: 'patientTeam', operator: 'is', value: teamId })
};

/*
The scope the chip set implies, mirroring the server: an explicit Scope chip wins;
a Type chip means clinic tasks; Protocol / Protocol status chips mean protocol tasks,
except "Protocol is empty" which is the clinic tasks. Nothing scope-bearing → all.
*/
export function getEffectiveScope(filters) {
    const scopeChip = find(filters, { field: 'scope' });
    if (scopeChip) {
        return scopeChip.value;
    }
    if (some(filters, { field: 'type' })) {
        return 'base';
    }
    const protocolChips = lodashFilter(filters, { field: 'protocol' });
    if (protocolChips.length > 0 && protocolChips.every((chip) => chip.operator === 'isEmpty')) {
        return 'base';
    }
    if (protocolChips.length > 0 || some(filters, { field: 'protocolStatus' })) {
        return 'protocols';
    }
    return 'all';
}

const ALL_STATUSES = ['OPEN', 'CLOSED'];

function statusesAllowedByChip(chip) {
    const values = Array.isArray(chip.value) ? chip.value : [chip.value];
    const positive = chip.operator === 'is' || chip.operator === 'isAnyOf';
    return ALL_STATUSES.filter((status) => values.includes(status) === positive);
}

// Which statuses the chip set can still return: no Status chip means both.
export function getStatusVisibility(filters) {
    const allowed = lodashFilter(filters, { field: 'status' }).reduce(
        (remaining, chip) => remaining.filter((status) => statusesAllowedByChip(chip).includes(status)),
        ALL_STATUSES
    );
    return { showsOpen: allowed.includes('OPEN'), showsClosed: allowed.includes('CLOSED') };
}

// Due date first, oldest first — unless the list can only show closed tasks, which
// reads best most recently closed first.
export function getDefaultSort(filters) {
    const { showsOpen, showsClosed } = getStatusVisibility(filters);
    if (showsClosed && !showsOpen) {
        return { sortField: 'closedAt', sortDirection: 'desc' };
    }
    return { sortField: 'dueDate', sortDirection: 'asc' };
}

export function formatCalendarDate(value) {
    if (!isCalendarDate(value)) {
        return '';
    }
    return format(parseISO(value), 'MMM d, yyyy');
}

export function toCalendarDate(date) {
    if (!(date instanceof Date) || !isValid(date)) {
        return null;
    }
    return format(date, CALENDAR_DATE_FORMAT);
}

export function fromCalendarDate(value) {
    return isCalendarDate(value) ? parseISO(value) : null;
}

function protocolTemplateLabel(template) {
    return template.enabled ? template.name : `${template.name} (disabled)`;
}

// Resolves a field's option list: fixed, terminology-dependent, or store-backed.
export function getFieldOptions(field, { term, clinicMembers = [], teams = [], protocolTemplates = [] }) {
    if (field.options === 'clinicMembers') {
        return clinicMembers.map((member) => ({ value: member.id, label: `${member.firstName} ${member.lastName}` }));
    }
    if (field.options === 'teams') {
        return teams.map((team) => ({ value: team.id, label: team.name }));
    }
    if (field.options === 'protocolTemplates') {
        // disabled templates still have instances, so they stay pickable, after the live ones
        return sortBy(protocolTemplates, [(template) => !template.enabled, 'name']).map((template) => ({
            value: template.id,
            label: protocolTemplateLabel(template)
        }));
    }
    if (typeof field.options === 'function') {
        return field.options(term);
    }
    return field.options ?? [];
}

function optionLabel(options, value) {
    return find(options, { value })?.label ?? String(value);
}

// The value segment of a chip as a person reads it.
export function formatFilterValue(filter, sources) {
    const field = getFilterField(filter.field);
    const kind = getOperatorValueKind(filter.operator);
    if (!field || kind === 'none' || isNil(filter.value)) {
        return '';
    }
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    switch (field.type) {
        case 'text':
            return `"${filter.value}"`;
        case 'list':
        case 'person': {
            const options = getFieldOptions(field, sources);
            return uniq(values.map((value) => optionLabel(options, value))).join(', ');
        }
        case 'date':
            return values.map(formatCalendarDate).join(' – ');
        case 'minutes':
            return kind === 'range' ? `${values[0]} – ${values[1]} min` : `${values[0]} min`;
        default:
            return String(filter.value);
    }
}
